import React, { useState } from 'react';
import cx from 'classnames';
import { find } from 'lodash';
import { Collapse } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';

import { paths } from 'config/path_helpers';

import { useQueries } from '@schools/app/queries';
import CategoryProfileList from '@schools/app/components/category_profile_list';
import CategoryProfileListTrigger from '@schools/app/components/category_profile_list/trigger';
import Contents from '@schools/app/components/contents';
import MetricsList from '@schools/app/components/metrics_list';
import MetricsSelect from '@schools/app/components/metrics_select';
import PageOverlay from '@schools/app/components/page_overlay';
import ProfileCardSet from '@schools/app/components/profile_card_set';
import Footer from '@schools/app/organisms/footer';
import profileGroups from '@schools/app/utilities/profile_groups';

import colors from '@magoosh/lib/styles/colors.module.scss';
import MagooshLogo from '@magoosh/assets/magoosh-logo-purple.png';
import sharedStyle from '../style.module.scss';

interface Props {
  preview?: boolean;
}

const Category: React.FC<Props> = (props) => {
  let { slug } = useParams();
  const q = useQueries();
  const [profileSelectIsOpen, setProfileSelectIsOpen] = useState<boolean>(false);

  const schoolProfileCategoryQuery = q.schoolProfileCategoryQuery(slug, !!props.preview);
  const category = schoolProfileCategoryQuery.data;

  const toggleProfileSelect = (value) => {
    if (value) {
      document.body.classList.add(sharedStyle.overflowHidden);
    } else {
      document.body.classList.remove(sharedStyle.overflowHidden);
    }

    setProfileSelectIsOpen(value);
  };

  return (
    <div className="d-flex flex-column" style={{ minHeight: '100vh' }}>
      {
        category && (
          <Helmet>
            {category.seoMetaDescription && <meta name="description" content={category.seoMetaDescription} />}
            <title>{category.name} - Overview & Rankings</title>
          </Helmet>
        )
      }

      <div className={cx(sharedStyle.fixedNavbar, { [sharedStyle.in]: profileSelectIsOpen })}>
        <div className="container align-items-center d-flex u-padding-V-m">
          <div>
            <a href="https://magoosh.com">
              <img alt="Magoosh" className={sharedStyle.logo} src={MagooshLogo} />
            </a>
          </div>

          <div className="flex-1">
            <div className="d-flex justify-content-end">
              <CategoryProfileListTrigger
                currentProfile={null}
                isOpen={profileSelectIsOpen}
                onTrigger={() => toggleProfileSelect(!profileSelectIsOpen)}
              />
            </div>
          </div>
        </div>

        <Collapse in={profileSelectIsOpen}>
          <div className={sharedStyle.categoryProfileListOverlay}>
            <CategoryProfileList
              category={category}
              className="u-padding-B-l"
              preview={props.preview}
            />
          </div>
        </Collapse>
      </div>

      <div className="flex-1" style={{ position: 'relative' }}>
        {<PageOverlay in={profileSelectIsOpen} onClick={() => toggleProfileSelect(false)} />}

        <div className="container">
          <div>
            {
              category && (
                <h1 className="u-margin-A-n" style={{ color: colors.grayDarker }}>
                  <strong>{category.name}</strong>
                </h1>
              )
            }
          </div>

          <div className="visible-xs">
            <hr />
            <strong>Metrics and Rankings</strong>
            <MetricsSelect
              category={category}
              currentMetric={null}
              metrics={category?.metrics}
              preview={props.preview}
            />
          </div>

          {
            category?.contents && (
              <div className="u-margin-T-l">
                <Contents contents={category?.contents} />
              </div>
            )
          }

          <div className={cx(sharedStyle.contents, 'd-flex')}>
            <div className={cx(sharedStyle.contentsSections, 'd-flex flex-column flex-1')}>
              {
                profileGroups(category).map((group, idx) => {
                  const profiles = group.profiles.map((profile) => find(category.profiles, (p) => p.slug === profile.slug));

                  return (
                    <div key={idx} className="u-margin-B-xl" style={{ position: 'relative' }}>
                      <h3 className="u-margin-T-n">{group.name}</h3>

                      <ProfileCardSet category={category} preview={props.preview} profiles={profiles} />
                    </div>
                  )
                })
              }
            </div>

            <div className={sharedStyle.contentsSidebar}>
              <div className="hidden-xs">
                <h4>Metrics and Rankings</h4>
                <MetricsList
                  category={category}
                  currentMetric={null}
                  metrics={category?.metrics}
                  preview={props.preview}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="u-margin-T-xl">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Category;
